import React, { useState } from "react";
import { Clock, X } from "lucide-react";
import type { Localizer } from "@/main/react/Types";
import { ClientFactory } from "@/main/common/ClientFactory";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { NativeSelect } from "@/components/ui/native-select";
import { ErrorBanner } from "@/main/react/component/ErrorBanner";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { LocalizationKey } from "@/main/util/LocalizationUtil";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import logger from "@/lib/logger";
import { getErrorMessage } from "@/main/react/header/sidebar/chat/chatShared";
import { GROUP_BAN_REASON_LOCALIZATION_KEYS } from "@/main/common/GroupBanReasons";
import type { GroupBoardPurgeWindow } from "@/main/common/GroupChatTypes";
import { showBanUserDialog } from "@/main/react/Dialog/GroupBanUserDialog";

/**
 * C2 of the group chat moderation toolkit (design 2026-08-28): time one member out of the ROOM.
 *
 * A sibling of `GroupBanUserDialog`, deliberately narrower: the same reason list, the same duration
 * steps MINUS "permanent" (a permanent timeout is a ban with extra steps, and the dialog says so with
 * its escalation link), and no free-text note (the note on a ban is a moderation record; a timeout
 * is a pause). The optional purge (C4) rides here because "quiet this person and clean up what they
 * just posted" is one decision made in one moment.
 *
 * The write goes through `ClientFactory` (rule 6); the caller re-reads the board view on success.
 */
const DURATIONS: { value: string; labelKey: LocalizationKey }[] = [
    { value: "ONE_DAY", labelKey: "groups.modal.ban.duration.one_day" },
    { value: "THREE_DAYS", labelKey: "groups.modal.ban.duration.three_days" },
    { value: "ONE_WEEK", labelKey: "groups.modal.ban.duration.one_week" },
    { value: "ONE_MONTH", labelKey: "groups.modal.ban.duration.one_month" },
];

const PURGE_WINDOWS: { value: GroupBoardPurgeWindow; labelKey: LocalizationKey }[] = [
    { value: "LAST_HOUR", labelKey: "group.chat.moderation.purge_window.hour" },
    { value: "LAST_DAY", labelKey: "group.chat.moderation.purge_window.day" },
    { value: "ALL", labelKey: "group.chat.moderation.purge_window.all" },
];

export function GroupTimeoutMemberDialogInner({
    groupPublicId,
    username,
    localizer,
    onClose,
    onSuccess,
}: {
    groupPublicId: string;
    username: string;
    localizer: Localizer;
    onClose: () => void;
    onSuccess?: () => void;
}) {
    const [duration, setDuration] = useState(DURATIONS[0].value);
    const [reasonType, setReasonType] = useState<string>("OTHER");
    const [purge, setPurge] = useState(false);
    const [purgeWindow, setPurgeWindow] = useState<GroupBoardPurgeWindow>("LAST_DAY");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function submit() {
        setError(null);
        setSaving(true);
        try {
            await ClientFactory.restrictGroupBoardMember(groupPublicId, username, duration, reasonType);
            if (purge) {
                await ClientFactory.purgeGroupBoardMemberMessages(groupPublicId, username, purgeWindow);
            }
            onSuccess?.();
            onClose();
        } catch (err) {
            logger.error("Failed to time out a group member", err);
            setError(getErrorMessage(err, localizer("group.chat.moderation.action_failed")));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                void submit();
            }}
            className="w-full"
            data-playwright-id="group-timeout-dialog"
        >
            <div className="flex flex-col gap-4">
                {error ? <ErrorBanner description={error} className="rounded-xl py-3" /> : null}
                <p className="text-sm text-muted-foreground">{localizer("group.chat.moderation.timeout_help")}</p>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium" htmlFor="group-timeout-duration">
                        {localizer("groups.modal.ban.duration.label")}
                    </label>
                    <NativeSelect
                        id="group-timeout-duration"
                        value={duration}
                        onChange={(event) => setDuration(event.target.value)}
                        disabled={saving}
                        data-playwright-id="group-timeout-duration"
                    >
                        {DURATIONS.map((option) => (
                            <option key={option.value} value={option.value}>{localizer(option.labelKey)}</option>
                        ))}
                    </NativeSelect>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium" htmlFor="group-timeout-reason">
                        {localizer("groups.modal.ban.reason_type.label")}
                    </label>
                    <NativeSelect
                        id="group-timeout-reason"
                        value={reasonType}
                        onChange={(event) => setReasonType(event.target.value)}
                        disabled={saving}
                    >
                        {Object.entries(GROUP_BAN_REASON_LOCALIZATION_KEYS).map(([value, key]) => (
                            <option key={value} value={value}>{localizer(key)}</option>
                        ))}
                    </NativeSelect>
                </div>

                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={purge}
                        onChange={(event) => setPurge(event.target.checked)}
                        disabled={saving}
                        data-playwright-id="group-timeout-purge"
                    />
                    <span>{localizer("group.chat.moderation.purge_checkbox")}</span>
                </label>
                {purge ? (
                    <NativeSelect
                        value={purgeWindow}
                        onChange={(event) => setPurgeWindow(event.target.value as GroupBoardPurgeWindow)}
                        disabled={saving}
                        aria-label={localizer("group.chat.moderation.purge_checkbox")}
                    >
                        {PURGE_WINDOWS.map((option) => (
                            <option key={option.value} value={option.value}>{localizer(option.labelKey)}</option>
                        ))}
                    </NativeSelect>
                ) : null}

                <button
                    type="button"
                    className="self-start text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
                    onClick={() => {
                        onClose();
                        showBanUserDialog(groupPublicId, localizer, username, onSuccess);
                    }}
                    disabled={saving}
                    data-playwright-id="group-timeout-escalate-ban"
                >
                    {localizer("group.chat.moderation.timeout_escalate")}
                </button>
            </div>

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={onClose}
                    text={localizer("literal.cancel")}
                    icon={X}
                    disabled={saving}
                    variant="outline"
                />
                {/* `type="button"` + explicit onClick, NOT `type="submit"`: SimpleDialogFooter
                    portals this button out of the <form>, so it has no form owner and a native
                    submit never fires. See the 2026-08-28 section of
                    designs/01-now/2026-05-31-simpledialog-sticky-footer.md. */}
                <CtaButton
                    type="button"
                    onClick={() => void submit()}
                    text={localizer("group.chat.moderation.timeout_confirm")}
                    icon={Clock}
                    disabled={saving}
                    isLoading={saving}
                    variant="destructive"
                    playwrightId="group-timeout-confirm"
                />
            </SimpleDialogFooter>
        </form>
    );
}

export function showGroupTimeoutMemberDialog(
    groupPublicId: string,
    localizer: Localizer,
    username: string,
    onSuccess?: () => void,
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div>{localizeWithMemberName(localizer, "group.chat.moderation.timeout_title", `@${username}`)}</div>}
            width="md"
        >
            <GroupTimeoutMemberDialogInner
                groupPublicId={groupPublicId}
                username={username}
                localizer={localizer}
                onClose={() => onOpenChange(false)}
                onSuccess={onSuccess}
            />
        </SimpleDialog>
    ));
}

export default showGroupTimeoutMemberDialog;
